import {Button, Chip} from '@jobseeker/lotus/elements';
import Loader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {
  Box,
  Typography,
  TextField as BaseTextField,
  styled,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  SurveyCheck as SurveyCheckYellow,
  ThumbsUpYellow,
  ThumbsDownGrey,
  GreenBag,
  ThumbsUpGreen,
  SurveyCheckGreen,
} from '../../assets/icons';
import {useEffect, useState} from 'react';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {parseString} from './utils';

import type {SurveyQuestionType, SurveyAnswerType} from './types';

const TextField = styled(BaseTextField)(({theme}) => ({
  'textarea::placeholder': {
    fontStyle: 'normal',
  },
  '.MuiInputBase-root': {
    fontSize: theme.breakpoints.up('sm') ? 12 : 10,
    lineHeight: '15px',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    with: '100%',
  },
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
  },
}));

const SurveyQuestion = ({
  currQuestion,
  questionsMapping,
  setCurrentQuestion,
  setShowSurvey,
  primaryColor,
  surveyTextColor,
  submitAnswer,
  surveyType,
}: SurveyQuestionType) => {
  const {question, cta, jumpTo, type, subType, options} = currQuestion;
  const ctaTexts = JSON.parse(cta);
  const jumpToMapping = JSON.parse(jumpTo);
  const optionsMapping = JSON.parse(options);
  const subTypes = JSON.parse(subType);
  const [selectedChips, setSelectedChips] = useState([]);
  const [textValue, setTextValue] = useState('');
  const [showTextError, setShowTextError] = useState(false);
  const [loading, setLoading] = useState(false);
  const isLargeDevice = useIsLargeDevice();
  const theme = useWrappedTheme();

  useEffect(() => {
    const isRelevantJob = surveyType === 'RelevantJob';
    const textLength = textValue?.length || 0;
    const showError = isRelevantJob
      ? (textLength > 0 && textLength < 10) || textLength > 200
      : textLength > 250;
    setShowTextError(showError);
  }, [textValue, surveyType]);

  const handleCTAClick = async (target: string) => {
    setLoading(true);
    const parsedCta = parseString(target, false) as string;

    const answer: {[key: string]: string} = {
      buttons: parsedCta,
    };
    if (selectedChips?.length) {
      answer['chips'] = JSON.stringify(selectedChips);
    }
    if (textValue?.length) {
      answer['text'] = textValue;
    }

    const answerResponse: SurveyAnswerType = await submitAnswer(
      JSON.stringify(answer)
    );

    if (answerResponse?.id) {
      setLoading(false);

      if (jumpToMapping[parsedCta]) {
        setCurrentQuestion(questionsMapping[jumpToMapping[parsedCta]]);
      } else {
        setShowSurvey(false);
      }

      setSelectedChips([]);
      setTextValue('');
    }
  };

  const replaceIconsFromText = (
    text: string,
    fontSize: string,
    fontWeight: number,
    fontFamily: string,
    lineHeight: string,
    mt: string
  ) => {
    const textComponent = (src: string) => {
      const source =
        src === '{ThumbsUpYellow}'
          ? ThumbsUpYellow
          : src === '{ThumbsDownGrey}'
          ? ThumbsDownGrey
          : src === '{SurveyCheckYellow}'
          ? SurveyCheckYellow
          : src === '{GreenBag}'
          ? GreenBag
          : src === '{ThumbsUpGreen}'
          ? ThumbsUpGreen
          : src === '{SurveyCheckGreen}'
          ? SurveyCheckGreen
          : null;

      if (source) {
        if (src === '{SurveyCheckYellow}') {
          return <Image src={source} width={48} height={48} alt="" />;
        } else if (src === '{SurveyCheckGreen}') {
          return <Image src={source} width={28} height={28} alt="" />;
        } else if (src === '{ThumbsUpGreen}') {
          return (
            <Image
              src={source}
              width={22}
              height={22}
              alt=""
              style={{marginBottom: '5px'}}
            />
          );
        } else if (src === '{ThumbsDownGrey}' && surveyType === 'RelevantJob') {
          return (
            <Image
              src={source}
              width={22}
              height={22}
              alt=""
              style={{marginTop: '5px'}}
            />
          );
        } else {
          return <Image src={source} width={22} height={22} alt="" />;
        }
      } else {
        return (
          <Typography
            fontFamily={fontFamily}
            lineHeight={lineHeight}
            fontSize={fontSize}
            fontWeight={fontWeight}
            mt={mt}
            component={'span'}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: src.replace(/\\n/g, '<br/>') as string,
              }}
            />
          </Typography>
        );
      }
    };

    const replacementText = parseString(text, true);

    return (
      <Typography
        component={'span'}
        display={'flex'}
        alignItems={'center'}
        gap={1}
        fontFamily={fontFamily}
        lineHeight={lineHeight}
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        {replacementText.map((text: string) => {
          if (text?.length) {
            return textComponent(text);
          }
        })}
      </Typography>
    );
  };

  return (
    <Box
      width={
        surveyType === 'RelevantJob' ? '100%' : isLargeDevice ? '70%' : '100%'
      }
      height={'100%'}
      display={'flex'}
      flexDirection={
        surveyType === 'RelevantJob' && type === 'singleSelect'
          ? 'row'
          : 'column'
      }
      gap={1.5}
      justifyContent={'space-between'}
      alignItems={
        surveyType === 'RelevantJob' && type === 'singleSelect'
          ? 'center'
          : 'flex-start'
      }
      flexWrap={'wrap'}
      sx={{
        zIndex: 2,
      }}
    >
      {type === 'noSelect' ? (
        <Typography
          component={'span'}
          lineHeight={surveyType === 'RelevantJob' ? '16px' : '30px'}
          fontSize={
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(12)
              : isLargeDevice
              ? theme.typography.pxToRem(24)
              : theme.typography.pxToRem(20)
          }
          fontWeight={surveyType === 'RelevantJob' ? 500 : 700}
          fontFamily={surveyType === 'RelevantJob' ? 'Poppins' : 'Abhaya Libre'}
          color={surveyTextColor}
        >
          {replaceIconsFromText(
            question,
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(12)
              : theme.typography.pxToRem(24),
            surveyType === 'RelevantJob' ? 500 : 700,
            surveyType === 'RelevantJob' ? 'Poppins' : 'Abhaya Libre',
            surveyType === 'RelevantJob' ? '16px' : '30px',
            '0px'
          )}
        </Typography>
      ) : (
        <Typography component={'span'}>
          {replaceIconsFromText(
            question,
            surveyType === 'RelevantJob'
              ? theme.typography.pxToRem(14)
              : isLargeDevice
              ? theme.typography.pxToRem(14)
              : theme.typography.pxToRem(12),
            500,
            'Poppins',
            isLargeDevice ? '20px' : '16px',
            '3px'
          )}
        </Typography>
      )}
      {subTypes.map((subtype: string) => {
        if (subtype === 'chips') {
          return (
            <Chip
              versionVariant={VersionVariants.VERSION_TWO}
              selectedChips={selectedChips}
              setSelectedChips={setSelectedChips}
              chipPrimaryColor={primaryColor}
              chipsData={optionsMapping['chips']}
            />
          );
        } else if (subtype === 'text' && selectedChips.length) {
          return (
            <Box
              display={'flex'}
              flexDirection={'column'}
              gap={0.75}
              alignItems={'flex-end'}
              width={'100%'}
            >
              <TextField
                sx={{
                  width: '100%',
                  borderRadius: 1.75,
                  // border: showTextError
                  //   ? '1px solid #D11124'
                  //   : '1px solid #ffffff',
                  background: '#ffffff',
                  fontSize: isLargeDevice
                    ? theme.typography.pxToRem(12)
                    : theme.typography.pxToRem(10),
                  fontWeight: 400,
                  lineHeight: '15px',
                  '& fieldset': {border: '1px solid transparent'},
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: showTextError ? '#D11124' : 'secondary.dark',
                    },
                  },
                }}
                multiline
                maxRows={5}
                rows={5}
                inputProps={{}}
                placeholder={optionsMapping['text']}
                value={textValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setTextValue(event?.target?.value);
                }}
              />
              <Typography
                variant="caption"
                lineHeight="15px"
                color={
                  surveyType === 'RelevantJob' ? 'text.secondary' : '#D11124'
                }
                display={showTextError ? 'flex' : 'none'}
              >
                {surveyType === 'RelevantJob'
                  ? 'min 10 and max 200 characters'
                  : 'Max 250 characters'}
              </Typography>
            </Box>
          );
        } else if (subtype === 'buttons') {
          return (
            <Box
              display={optionsMapping['buttons']?.length ? 'flex' : 'none'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              gap={0.75}
            >
              {optionsMapping['buttons'].map((buttonText: string) => {
                return (
                  <Button
                    disabled={
                      loading
                        ? true
                        : buttonText === 'Cancel'
                        ? false
                        : showTextError
                        ? true
                        : false
                    }
                    variant="outlined"
                    onClick={() => {
                      handleCTAClick(buttonText);
                    }}
                    sx={{
                      minWidth: surveyType === 'RelevantJob' ? '72px' : '130px',
                      paddingX: 2,
                      paddingY: 0.5,
                      border: buttonText === 'Cancel' ? `0px` : `1px solid`,
                      borderColor: `${primaryColor}`,
                      borderRadius: 8,
                      background: '#ffffff',
                      color: 'secondary.main',
                      fontSize: theme.typography.pxToRem(12),
                      fontWeight: 500,
                      lineHeight: '24px',
                      ':hover': {
                        background: '#ffffff',
                        borderColor: `${primaryColor}`,
                        boxShadow:
                          buttonText === 'Cancel'
                            ? '0px 0px 0px 0px #29292914'
                            : '0px 4px 4px 0px #29292914',
                      },
                      ':disabled': {
                        color:
                          buttonText === 'Cancel'
                            ? 'secondary.main'
                            : '#C0C1C2',
                        borderColor:
                          buttonText === 'Cancel' ? 'transparent' : '#E0E0E0',
                        background:
                          buttonText === 'Cancel' ? 'transparent' : '#E0E0E0',
                      },
                    }}
                  >
                    {!loading ? (
                      replaceIconsFromText(
                        buttonText,
                        theme.typography.pxToRem(12),
                        500,
                        'Poppins',
                        '24px',
                        '0px'
                      )
                    ) : (
                      <Loader size={20} sx={{color: primaryColor}} />
                    )}
                  </Button>
                );
              })}
            </Box>
          );
        }
      })}
      <Box
        display={ctaTexts?.length ? 'flex' : 'none'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        gap={0.75}
      >
        {ctaTexts.map((ctaText: string) => {
          return (
            <Button
              disabled={
                loading
                  ? true
                  : ctaText === 'Cancel'
                  ? false
                  : showTextError
                  ? true
                  : subType.includes('chips')
                  ? selectedChips.length
                    ? false
                    : true
                  : false
              }
              variant="outlined"
              onClick={() => {
                handleCTAClick(ctaText);
              }}
              sx={{
                minWidth: '78px',
                paddingX: 2,
                paddingY: 0.5,
                border: ctaText === 'Cancel' ? '0px' : `1px solid`,
                marginLeft: ctaText === 'Cancel' ? '-10px' : `0px`,
                borderColor:
                  ctaText === 'Cancel' ? 'transparent' : `${primaryColor}`,
                borderRadius: 8,
                background: ctaText === 'Cancel' ? 'transparent' : '#ffffff',
                color: 'secondary.main',
                fontSize: theme.typography.pxToRem(12),
                fontWeight: 500,
                lineHeight: '24px',
                ':hover': {
                  background: ctaText === 'Cancel' ? 'transparent' : '#ffffff',
                  border: ctaText === 'Cancel' ? '0px' : `1px solid`,
                  borderColor:
                    ctaText === 'Cancel' ? 'transparent' : `${primaryColor}`,
                  boxShadow:
                    ctaText === 'Cancel'
                      ? '0px 0px 0px 0px #29292914'
                      : '0px 4px 4px 0px #29292914',
                },
                ':disabled': {
                  color: ctaText === 'Cancel' ? 'secondary.main' : '#C0C1C2',
                  borderColor: ctaText === 'Cancel' ? 'transparent' : '#E0E0E0',
                  background: ctaText === 'Cancel' ? 'transparent' : '#E0E0E0',
                },
              }}
            >
              {!loading || ctaText === 'Cancel' ? (
                replaceIconsFromText(
                  ctaText,
                  theme.typography.pxToRem(12),
                  500,
                  'Poppins',
                  '24px',
                  '0px'
                )
              ) : (
                <Loader size={20} sx={{color: primaryColor}} />
              )}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default SurveyQuestion;
